import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import * as MeetingService from "../../../../services/MeetingService";
import * as CourseService from "../../../../services/CourseService";
import { useTranslation } from "react-i18next";
import MeetingFormNew from "./meetingFormNew.view";
import PATHS from "../../../../constants/paths";
import { ShowAlertDialog } from "@de-prado-dev/react-component-library";
import * as SettingService from "../../../../services/SettingService";

const MeetingFormNewContainer = (props) => {
  const { uuid } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [initValues, setInitValues] = useState({});
  const [courses, setCourses] = useState([]);
  const [centers, setCenters] = useState([]);
  const [centersSelected, setCentersSelected] = useState([]);
  const [filter, setFilter] = useState("");
  const [centersBack, setCentersBack] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedToMove, setSelectedToMove] = useState([]);
  const [selectedCourseUuid, setSelectedCourseUuid] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const courses = await CourseService.listCourse({});
      setCourses(courses);

      if (uuid) {
        const result = await MeetingService.getMeeting(uuid);
        if (result) {
          console.log(result);
          const dataFinal = {
            courseUuid: result.meeting.courseUuid,
            month: result.meeting.month,
            year: result.meeting.year,
          };

          setInitValues(dataFinal);
          setSelectedCourseUuid(result.meeting.courseUuid);

          // Find the course to get its centers
          const selectedCourse = courses.find(
            (c) => c.uuid === result.meeting.courseUuid
          );

          // Set selected centers if available
          if (result.meeting.centers && selectedCourse?.centers) {
            const centersFormattedData = selectedCourse.centers.map((c) => ({
              value: c.uuid,
              label: c.name,
            }));

            const selectedCenters = centersFormattedData.filter((c) =>
              result.meeting.centers.includes(c.value)
            );
            setCentersSelected(selectedCenters);

            // Remove selected centers from available list
            const availableCenters = centersFormattedData.filter(
              (c) => !result.meeting.centers.includes(c.value)
            );
            setCenters(availableCenters);
          }
        }
      }
    };

    loadData();
  }, [uuid]);

  // Effect to handle course selection change
  useEffect(() => {
    if (selectedCourseUuid && courses.length > 0) {
      const selectedCourse = courses.find((c) => c.uuid === selectedCourseUuid);

      if (selectedCourse?.centers && Array.isArray(selectedCourse.centers)) {
        // Convert centers to the format needed (value, label)
        const centersFormattedData = selectedCourse.centers.map((c) => ({
          value: c.uuid,
          label: c.name,
        }));

        // Sort by label
        const sortedCenters = centersFormattedData.sort((a, b) =>
          a.label.localeCompare(b.label)
        );

        setCenters(sortedCenters);
        // Reset selected centers when course changes (only in creation mode)
        if (!uuid) {
          setCentersSelected([]);
          setSelectedAvailable([]);
          setFilter("");
          setCentersBack([]);
        }
      }
    }
  }, [selectedCourseUuid, courses, uuid]);

  const handleSubmit = async (values) => {
    try {
      if (values) {
        // Validación: asegurar que hay al menos un centro seleccionado (solo en modo creación)
        if (!uuid && (!centersSelected || centersSelected.length === 0)) {
          ShowAlertDialog(
            "Error",
            t("forms.meeting.messages.noLocationsError"),
            "error",
            false,
            "OK",
            false,
            null
          );
          return;
        }

        let result = undefined;
        let data = {};

        if (uuid) {
          data = {
            courseUuid: values.courseUuid,
            month: values.month,
            year: values.year,
          };
          result = await MeetingService.updateMeeting(data, uuid);
        } else {
          data = {
            courseUuid: values.courseUuid,
            month: values.month,
            farm: centersSelected[0]?.value || "",
            centers: centersSelected.map((c) => c.value),
            speakers: [],
            year: values.year,
          };
          result = await MeetingService.addMeeting(data);
        }

        let title = "";
        let text = "";
        let icon = "";
        if (result) {
          if (uuid) {
            title = t("forms.meeting.messages.update.title");
            text = t("forms.meeting.messages.update.success");
            icon = "success";
          } else {
            title = t("forms.meeting.messages.create.title");
            text = t("forms.meeting.messages.create.success");
            icon = "success";
          }
        } else {
          title = "Error";
          text = t("forms.meeting.messages.update.error");
          icon = "error";
        }

        let okAction = () => {
          navigate(PATHS.LIST_MEETING);
        };
        ShowAlertDialog(title, text, icon, false, "OK", false, okAction);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      let title = "Error";
      let text = t("forms.meeting.messages.update.error");
      let icon = "error";

      const backendCode =
        error?.data?.error?.code || error?.response?.data?.error?.code;

      switch (backendCode) {
        case "MEETING.MONTH.INVALID":
          text = t("forms.meeting.messages.error.meetingMonthInvalid");
          break;

        default:
          if (error?.response?.data?.message) {
            text = error.response.data.message;
          }
          break;
      }

      ShowAlertDialog(title, text, icon, false, "OK", false, null);
    }
  };

  const handleOnback = () => {
    navigate(PATHS.LIST_MEETING);
  };

  const handlerOnSelectCenter = async (center) => {
    if (!center) return;
    const newSelected = [...centersSelected];
    newSelected.push(center);
    const newAllowed = [...centers];
    const index = newAllowed.findIndex((obj) => obj.value === center.value);
    if (index !== -1) {
      newAllowed.splice(index, 1);
    }
    setCentersSelected(newSelected);
    setCenters(newAllowed);
    setFilter("");
    setCentersBack([]);
    setSelectedAvailable([]);
  };

  const handlerOnSelectAllCenter = async () => {
    if (!centers || centers.length === 0) return;
    const locationsToAdd =
      centersBack && centersBack.length > 0 && filter.length > 0
        ? centersBack
        : centers;

    const newSelected = [...centersSelected, ...locationsToAdd];
    const newCenters = centers.filter((c) => !locationsToAdd.includes(c));
    setCentersSelected(newSelected);
    setCenters(newCenters);
    setFilter("");
    setCentersBack([]);
    setSelectedAvailable([]);
  };

  const handlerOnDeleteCenter = async (center) => {
    if (!center) return;
    const newAllowed = [...centers];
    newAllowed.push(center);

    const newSelected = [...centersSelected];
    const index = newSelected.findIndex((obj) => obj.value === center.value);
    if (index !== -1) {
      newSelected.splice(index, 1);
    }
    setCentersSelected(arrayOrder(newSelected, "label"));
    setCenters(arrayOrder(newAllowed, "label"));
    setSelectedToMove([]);
  };

  const handlerOnDeleteAllCenter = async () => {
    if (!centersSelected || centersSelected.length === 0) return;
    const newAllowed = [...centers, ...centersSelected];
    setCentersSelected([]);
    setCenters(arrayOrder(newAllowed, "label"));
    setSelectedToMove([]);
  };

  const handleMoveSelected = () => {
    if (!selectedAvailable || selectedAvailable.length === 0) return;
    const newSelected = [...centersSelected, ...selectedAvailable];
    const newCenters = centers.filter((c) => !selectedAvailable.includes(c));
    setCentersSelected(newSelected);
    setCenters(newCenters);
    setSelectedAvailable([]);
    setFilter("");
    setCentersBack([]);
  };

  const handleMoveBackSelected = () => {
    if (!selectedToMove || selectedToMove.length === 0) return;
    const newAllowed = [...centers, ...selectedToMove];
    const newSelected = centersSelected.filter(
      (c) => !selectedToMove.includes(c)
    );
    setCentersSelected(newSelected);
    setCenters(arrayOrder(newAllowed, "label"));
    setSelectedToMove([]);
  };

  const handleToggleAvailable = (center, isCtrlKey) => {
    if (isCtrlKey) {
      // Multi-selección con Ctrl
      const isSelected = selectedAvailable.some(
        (c) => c.value === center.value
      );
      if (isSelected) {
        setSelectedAvailable(
          selectedAvailable.filter((c) => c.value !== center.value)
        );
      } else {
        setSelectedAvailable([...selectedAvailable, center]);
      }
    } else {
      // Selección simple
      setSelectedAvailable([center]);
    }
  };

  const handleToggleToMove = (center, isCtrlKey) => {
    if (isCtrlKey) {
      // Multi-selección con Ctrl
      const isSelected = selectedToMove.some((c) => c.value === center.value);
      if (isSelected) {
        setSelectedToMove(
          selectedToMove.filter((c) => c.value !== center.value)
        );
      } else {
        setSelectedToMove([...selectedToMove, center]);
      }
    } else {
      // Selección simple
      setSelectedToMove([center]);
    }
  };

  const arrayOrder = (array, sortBy) => {
    return array.sort((a, b) => {
      if (a[sortBy] < b[sortBy]) return -1;
      if (a[sortBy] > b[sortBy]) return 1;
      return 0;
    });
  };

  const filterCenter = (value) => {
    if (value.length > 0) {
      const regex = new RegExp(value, "i");
      const toFilter = [...centers];
      const newCenters = toFilter.filter((comp) => regex.test(comp.label));
      setFilter(value);
      setCentersBack(newCenters);
    } else {
      setFilter("");
      setCentersBack([]);
    }
  };

  return (
    <MeetingFormNew
      edit={uuid ? true : false}
      initialValues={initValues}
      courses={courses}
      onSubmit={handleSubmit}
      onBack={handleOnback}
      onSelect={handlerOnSelectCenter}
      onDelete={handlerOnDeleteCenter}
      onSelectAll={handlerOnSelectAllCenter}
      onDeleteAll={handlerOnDeleteAllCenter}
      filterCenter={filterCenter}
      centersBack={centersBack}
      filter={filter}
      centers={centers}
      centersSelected={centersSelected}
      selectedAvailable={selectedAvailable}
      selectedToMove={selectedToMove}
      onToggleAvailable={handleToggleAvailable}
      onToggleToMove={handleToggleToMove}
      onMoveSelected={handleMoveSelected}
      onMoveBackSelected={handleMoveBackSelected}
      onCourseChange={setSelectedCourseUuid}
    />
  );
};

MeetingFormNewContainer.propTypes = {
  uuid: PropTypes.string,
};

export default MeetingFormNewContainer;
